/// <amd-module name="Wit/WorkItemForm/Links/Controls/CompactLinksList" />
import * as React from "react";
import { PropsWithChildren } from "react";
import { useService } from "VSS/Platform/Layout";
import { Button } from "VSSUI/Button";
import { IconSize } from "VSSUI/Icon";
import { IWorkItemLinksService } from "Wit/Common/WorkItemLinksService/WorkItemLinksService";
import * as Resources from "Wit/WorkItemForm/Resources";
import { ILinkIdentifier, IResolvedLinkDisplayData } from "Wit/WorkItemLinksRegistration/Contracts";
import { ArtifactLink } from "./ArtifactLink";
import { ArtifactLinkPlaceholder } from "./ArtifactLinkPlaceholder";

export interface ICompactLinksListProps {
    links: (IResolvedLinkDisplayData | undefined)[];
    onDeleteLink: (linkIdentifier: ILinkIdentifier) => void;
    onDismissWorkItemForm: () => void;
}

export function CompactLinksList({ links, onDeleteLink, onDismissWorkItemForm }: ICompactLinksListProps) {
    const linksService = useService<IWorkItemLinksService>("IWorkItemLinksService");
    const [groups, placeholders] = groupByLinkType(links, linksService);

    return (
        <div className="compact-links-list">
            {groups.map(group => (
                <div className="link-type-group" key={`link-type-[${group.linkType}]-group`}>
                    <div className="link-type-name padding-bottom-8">{group.linkTypeDisplayName}</div>
                    <div className="link-type-links">
                        {group.links.map(link => (
                            <RemovableItemContainer
                                key={`link=[${link.id}_${link.uri}_${link.linkIdentifier.ExtID}]`}
                                onRemove={() => onDeleteLink(link.linkIdentifier)}
                            >
                                <ArtifactLink
                                    className="artifact-link padding-bottom-8"
                                    artifactData={link}
                                    showDetails
                                    onDismissWorkItemForm={onDismissWorkItemForm}
                                />
                            </RemovableItemContainer>
                        ))}
                    </div>
                </div>
            ))}

            {placeholders.length > 0 && (
                <div className="link-type-group" key={`link-type-[placeholder]-group`}>
                    <div className="link-type-links">
                        {placeholders.map((p, i) => (
                            <ArtifactLinkPlaceholder key={i} className="padding-bottom-8" showIcon showDetails />
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

function RemovableItemContainer({ children, onRemove }: PropsWithChildren<{ onRemove: () => void }>) {
    return (
        <div className="removable-item-container full-width">
            {children}
            <div className="remove-item-button padding-8">
                <Button
                    subtle
                    onClick={onRemove}
                    iconProps={{ iconName: "ChromeClose", size: IconSize.small }}
                    tooltipProps={{ text: Resources.DeleteLink }}
                />
            </div>
        </div>
    );
}

interface IGroup {
    linkType: string; // A immutable name for a work item link or a registered link type name otherwise
    linkTypeDisplayName: string;
    links: IResolvedLinkDisplayData[];
}

function groupByLinkType(links: (IResolvedLinkDisplayData | undefined)[], linksService: IWorkItemLinksService): [IGroup[], undefined[]] {
    const resolvedDisplayData = links.filter(l => !!l) as IResolvedLinkDisplayData[];
    const placeholders = links.filter(l => !l) as undefined[];

    const groups: IGroup[] = [];
    for (const link of resolvedDisplayData) {
        const linkType = link.witLinkTypeEnd ?? link.registeredLinkType;
        const matchingGroup = groups.find(group => group.linkType === linkType);
        if (matchingGroup) {
            matchingGroup.links.push(link);
        } else {
            groups.push({
                linkType,
                linkTypeDisplayName: link.linkTypeDisplayName,
                links: [link]
            });
        }
    }

    const sorter = linksService.makeLinkComparer<IGroup>(group => {
        return group.linkType;
    });
    groups.sort(sorter.getComparer());

    return [groups, placeholders];
}
